import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createPilotTabCoordinator,
  PilotTabCoordinator,
} from '../../utils/pilot-tab-coordinator';

interface UsePilotTabOwnershipOptions {
  activityId?: string | null;
  enabled?: boolean;
  onStreamStolen?: (activityId: string) => void;
}

export function usePilotTabOwnership({
  activityId,
  enabled = true,
  onStreamStolen,
}: UsePilotTabOwnershipOptions) {
  const coordinator = useMemo<PilotTabCoordinator>(
    () => createPilotTabCoordinator(),
    [],
  );
  const lastClaimedActivityIdRef = useRef<string | null>(null);
  const activityIdRef = useRef(activityId);
  const enabledRef = useRef(enabled);
  const onStreamStolenRef = useRef(onStreamStolen);
  const [isOwner, setIsOwner] = useState(false);
  const [isPassive, setIsPassive] = useState(false);

  useEffect(() => {
    activityIdRef.current = activityId;
    enabledRef.current = enabled;
    onStreamStolenRef.current = onStreamStolen;
  }, [activityId, enabled, onStreamStolen]);

  const releaseStream = useCallback(() => {
    const currentActivityId = lastClaimedActivityIdRef.current;
    if (!enabled || !currentActivityId) {
      return;
    }

    coordinator.releaseStream(currentActivityId);
    lastClaimedActivityIdRef.current = null;
    setIsOwner(false);
    setIsPassive(coordinator.isStreamClaimedByOtherTab(currentActivityId));
  }, [coordinator, enabled]);

  const refreshOwnership = useCallback(() => {
    if (!enabled || !activityId) {
      setIsOwner(false);
      setIsPassive(false);
      return false;
    }

    const claimedByOtherTab = coordinator.isStreamClaimedByOtherTab(activityId);
    setIsPassive(claimedByOtherTab);
    if (claimedByOtherTab) {
      setIsOwner(false);
    }
    return claimedByOtherTab;
  }, [activityId, coordinator, enabled]);

  const claimStream = useCallback(() => {
    if (!enabled || !activityId) {
      setIsOwner(false);
      setIsPassive(false);
      return true;
    }

    if (lastClaimedActivityIdRef.current === activityId) {
      setIsOwner(true);
      setIsPassive(false);
      return true;
    }

    const claimed = coordinator.claimStream(activityId);
    if (claimed) {
      lastClaimedActivityIdRef.current = activityId;
      setIsOwner(true);
      setIsPassive(false);
      return true;
    }

    lastClaimedActivityIdRef.current = null;
    setIsOwner(false);
    setIsPassive(true);
    return false;
  }, [activityId, coordinator, enabled]);

  const forceClaimStream = useCallback(() => {
    if (!enabled || !activityId) {
      setIsOwner(false);
      setIsPassive(false);
      return true;
    }

    coordinator.forceClaimStream(activityId);
    lastClaimedActivityIdRef.current = activityId;
    setIsOwner(true);
    setIsPassive(false);
    return true;
  }, [activityId, coordinator, enabled]);

  useEffect(() => {
    coordinator.onStreamStolen(stolenActivityId => {
      if (!enabledRef.current || stolenActivityId !== activityIdRef.current) {
        return;
      }

      lastClaimedActivityIdRef.current = null;
      setIsOwner(false);
      setIsPassive(true);
      onStreamStolenRef.current?.(stolenActivityId);
    });

    coordinator.onStreamReleased(releasedActivityId => {
      if (
        !enabledRef.current ||
        releasedActivityId !== activityIdRef.current
      ) {
        return;
      }

      setIsPassive(coordinator.isStreamClaimedByOtherTab(releasedActivityId));
    });
  }, [coordinator]);

  useEffect(() => {
    if (!enabled || !activityId) {
      setIsOwner(false);
      setIsPassive(false);
      return;
    }

    const previousActivityId = lastClaimedActivityIdRef.current;
    if (previousActivityId && previousActivityId !== activityId) {
      coordinator.releaseStream(previousActivityId);
      lastClaimedActivityIdRef.current = null;
    }

    setIsOwner(false);
    setIsPassive(coordinator.isStreamClaimedByOtherTab(activityId));
  }, [activityId, coordinator, enabled]);

  useEffect(() => {
    return () => {
      releaseStream();
      coordinator.destroy();
    };
  }, [coordinator, releaseStream]);

  return {
    isOwner,
    isPassive,
    claimStream,
    forceClaimStream,
    releaseStream,
    refreshOwnership,
  };
}
